import { useDispatch, useSelector } from "react-redux";
import { RegistrationFormData, RegistrationFormSteps } from "./components/steps/StepModel";
import * as Yup from "yup";
import {
  selectCurrentStep,
  selectLoading,
  decrementCurrentStep,
  selectStatus,
} from "./components/RegistrationPageSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import { Form, Formik, FormikHelpers } from "formik";
import { RegistrationStep } from "./components/RegistrationStep";
import { EmailAddressSubmit } from "./components/steps/EmailAddressStep";
import { EmailVerificationSubmit } from "./components/steps/EmailVerificationStep";
import { PasswordSubmit } from "./components/steps/PasswordStep";
import { NamesSubmit } from "./components/steps/NamesStep";
import { Link, useParams } from "react-router-dom";
import { selectCompanyBranding } from "app/modules/system/whiteLabelling/CompanyBrandingSlice";
import { passwordValidationRegex } from "cpl-react-common/components/Constants";

const initialValues: RegistrationFormData = {
  emailAddress: "",
  verificationCode: "",
  password: "",
  passwordConfirmation: "",
  firstName: "",
  lastName: "",
};

const validationSchema = {
  [RegistrationFormSteps.EmailAddress]: Yup.object().shape({
    emailAddress: Yup.string().email("Wrong email format").required("Email address is required"),
  }),
  [RegistrationFormSteps.EmailVerification]: Yup.object().shape({
    verificationCode: Yup.string().required("Email verification code is required"),
  }),
  [RegistrationFormSteps.Password]: Yup.object().shape({
    password: Yup.string()
      .required("Password is required")
      .matches(
        passwordValidationRegex,
        "Password must be at least 8 characters in length and contain one number, one upper case letter and one lower case letter"
      ),
    passwordConfirmation: Yup.string()
      .required("Password confirmation is required")
      .oneOf([Yup.ref("password")], "Passwords must match"),
  }),
  [RegistrationFormSteps.Names]: Yup.object().shape({
    firstName: Yup.string().max(100, "Maximum 100 symbols").required("First name is required"),
    lastName: Yup.string().max(100, "Maximum 100 symbols").required("Last name is required"),
  }),
};

const submitFunctions = {
  [RegistrationFormSteps.EmailAddress]: EmailAddressSubmit,
  [RegistrationFormSteps.EmailVerification]: EmailVerificationSubmit,
  [RegistrationFormSteps.Password]: PasswordSubmit,
  [RegistrationFormSteps.Names]: NamesSubmit,
};

export const RegistrationPage = () => {
  const loading = useSelector(selectLoading);
  const currentStep = useSelector(selectCurrentStep);
  const status = useSelector(selectStatus);
  const companyBranding = useSelector(selectCompanyBranding);
  const { emailAddress } = useParams();

  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();

  return (
    <Formik
      initialValues={{ ...initialValues, emailAddress: emailAddress ?? initialValues.emailAddress }}
      validationSchema={validationSchema[currentStep]}
      onSubmit={(values: RegistrationFormData, helpers: FormikHelpers<RegistrationFormData>) => {
        submitFunctions[currentStep](values, dispatch, thunkDispatch);
        helpers.setTouched({});
      }}
    >
      {(formikProps) => (
        <Form>
          {/* begin::Heading */}
          <div className="text-center mb-10">
            <h1 className="heading-text cpl-text-primary mb-3">{`Register with ${
              companyBranding?.clientPortalName ?? "CPL Client Portal"
            }`}</h1>
          </div>
          {/* begin::Heading */}
          {status && (
            <div className="mb-lg-6 alert alert-danger">
              <div className="alert-text font-weight-bold">{status}</div>
            </div>
          )}
          <RegistrationStep currentStep={currentStep} formikProps={formikProps} />
          {/* begin::Action */}
          <div className="text-center">
            <button
              type="submit"
              id="kt_sign_in_submit"
              className="cpl-btn btn-lg cpl-btn-primary w-100 mb-5"
              disabled={loading}
              data-test-id="registration-form__submit-button"
            >
              {loading ? (
                <span className="indicator-progress" style={{ display: "block" }}>
                  Please wait...
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                </span>
              ) : (
                <span className="indicator-label">Continue</span>
              )}
            </button>
            {(currentStep === RegistrationFormSteps.EmailVerification ||
              currentStep === RegistrationFormSteps.Names) && (
              <button
                type="button"
                onClick={() => dispatch(decrementCurrentStep())}
                className="cpl-btn btn-lg cpl-btn-secondary w-100 mb-5"
                disabled={loading}
                data-test-id="registration-form__back-button"
              >
                <span className="indicator-label">Back</span>
              </button>
            )}
            {currentStep === 0 && (
              <Link
                to={`/auth/login/${formikProps.values.emailAddress}`}
                className="cpl-link cpl-text-primary fw-600 d-inline-block fs-7 mt-4"
                data-test-id="registration-form__login-link"
              >
                Already have an account? Login here
              </Link>
            )}
          </div>
          {/* end::Action */}
        </Form>
      )}
    </Formik>
  );
};
